import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  List,
  useDataProvider,
  useNotify,
  useRedirect,
  ListContextProvider,
  useListController,
  TextInput,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
} from 'react-admin';
import { useTenant } from '../../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Paper,
  IconButton,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { PageTitle } from '../../components/PageTitle';
import { getCustomerTypeColor } from '../../utils/customerTypeColors';

const GOOGLE_MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';
const libraries: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = ['places'];

interface Location {
  id: string;
  name?: string;
  streetLine1: string;
  streetLine2?: string;
  city: string;
  province?: string;
  postalCode?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  isActive: boolean;
  active?: boolean;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
  zoneId?: string;
  zone?: {
    id: string;
    name: string;
    color?: string;
  };
  customerLocations?: CustomerLocation[];
  locationFiles?: Array<{
    id: string;
    category?: string;
    description?: string;
    file: {
      id: number;
      name: string;
      url?: string;
    };
  }>;
}

interface CustomerLocation {
  id: string;
  type: string;
  isPrimary: boolean;
  customer: {
    id: string;
    displayName: string;
    company?: string;
    customerType?: {
      id: string;
      name: string;
      color?: string;
    };
    phones?: Array<{
      phone: string;
      isPrimary: boolean;
    }>;
    emails?: Array<{
      email: string;
      isPrimary: boolean;
    }>;
  };
}

interface LocationWithCoordinates extends Location {
  latitude: number;
  longitude: number;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 43.6532, // Toronto, Canada
  lng: -79.3832,
};

export const SitesList: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [locationsWithCoords, setLocationsWithCoords] = useState<LocationWithCoordinates[]>([]);
  const [visibleLocations, setVisibleLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<LocationWithCoordinates | null>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  
  // Filter states
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [zoneFilter, setZoneFilter] = useState<string>('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [customerTypes, setCustomerTypes] = useState<any[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [zones, setZones] = useState<any[]>([]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-script-sites',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  // Load all locations with customer data
  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      // Build filter object based on active filters
      const filter: any = { isActive: true };
      
      if (zoneFilter) {
        filter.zoneId = zoneFilter;
      }
      if (customerFilter) {
        filter.customerId = customerFilter;
      }
      if (customerTypeFilter) {
        filter.customerTypeId = customerTypeFilter;
      }
      if (cityFilter) {
        filter.city = cityFilter;
      }
      
      // Load locations from the API
      const response = await dataProvider.getList('crm/locations', {
        filter,
        sort: { field: 'city', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
      });

      // Process locations - customer data is now embedded from the API
      const locationsWithCustomers = response.data.map((location: any) => {
        // Handle different data structures
        const locationData = {
          id: location.id,
          name: location.name || location.streetLine1 || location.street,
          streetLine1: location.streetLine1 || location.street || '',
          streetLine2: location.streetLine2 || '',
          city: location.city || '',
          province: location.province || location.state || '',
          postalCode: location.postalCode || location.zipCode || '',
          country: location.country || 'USA',
          latitude: location.latitude,
          longitude: location.longitude,
          isActive: location.isActive !== false,
          customerLocations: location.customerLocations || [],
        };
        
        // If there's a customerId directly on the location (for customer-addresses endpoint)
        if (location.customerId && !location.customerLocations?.length) {
          locationData.customerLocations = [{
            id: `${location.id}-${location.customerId}`,
            type: location.type || 'Primary',
            isPrimary: location.isPrimary || false,
            customer: location.customer || { id: location.customerId, displayName: 'Unknown' },
          }];
        }
        
        return locationData;
      });

      // Sort locations alphabetically by name or street address
      const sortedLocations = locationsWithCustomers.sort((a, b) => {
        const nameA = (a.name || a.streetLine1 || '').toLowerCase();
        const nameB = (b.name || b.streetLine1 || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setLocations(sortedLocations);
      setFilteredLocations(sortedLocations);
      setVisibleLocations(sortedLocations);
      
      // Extract unique cities for filter
      const uniqueCities = [...new Set(locationsWithCustomers.map(loc => loc.city).filter(Boolean))];
      setCities(uniqueCities.sort());
      
    } catch (error) {
      notify('Error loading locations', { type: 'error' });
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify, zoneFilter, customerFilter, customerTypeFilter, cityFilter]);

  // Load customers, customer types, and zones for filters
  const loadFilterData = useCallback(async () => {
    try {
      // Load each resource separately to handle failures gracefully
      
      // Load customers
      try {
        const customersResponse = await dataProvider.getList('crm/customers', {
          filter: {},
          sort: { field: 'displayName', order: 'ASC' },
          pagination: { page: 1, perPage: 1000 },
        });
        setCustomers(customersResponse.data);
      } catch (error) {
        console.error('Error loading customers:', error);
        setCustomers([]);
      }

      // Load customer types
      try {
        const customerTypesResponse = await dataProvider.getList('crm/customer-types', {
          filter: { isActive: true },
          sort: { field: 'name', order: 'ASC' },
          pagination: { page: 1, perPage: 100 },
        });
        setCustomerTypes(customerTypesResponse.data);
      } catch (error) {
        console.error('Error loading customer types:', error);
        setCustomerTypes([]);
      }

      // Load zones
      try {
        const zonesResponse = await dataProvider.getList('crm/zones', {
          filter: { isActive: true },
          sort: { field: 'name', order: 'ASC' },
          pagination: { page: 1, perPage: 100 },
        });
        setZones(zonesResponse.data);
      } catch (error) {
        console.error('Error loading zones:', error);
        setZones([]);
      }
    } catch (error) {
      console.error('Error in loadFilterData:', error);
    }
  }, [dataProvider]);

  // Geocode address
  const geocodeAddress = useCallback(async (location: Location): Promise<LocationWithCoordinates | null> => {
    if (!isLoaded || !window.google) {
      return null;
    }

    // If we already have coordinates, use them
    if (location.latitude && location.longitude) {
      return {
        ...location,
        latitude: location.latitude,
        longitude: location.longitude,
      };
    }

    const geocoder = new window.google.maps.Geocoder();
    const fullAddress = [
      location.streetLine1,
      location.streetLine2,
      location.city,
      location.province,
      location.postalCode,
      location.country,
    ]
      .filter(Boolean)
      .join(', ');

    return new Promise((resolve) => {
      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          resolve({
            ...location,
            latitude: loc.lat(),
            longitude: loc.lng(),
          });
        } else {
          // Skip locations that can't be geocoded - they likely have incomplete address data
          resolve(null);
        }
      });
    });
  }, [isLoaded]);

  // Geocode all filtered locations
  const geocodeAllLocations = useCallback(async () => {
    if (!isLoaded) {
      return;
    }
    
    if (filteredLocations.length === 0) {
      setLocationsWithCoords([]);
      return;
    }

    const geocodedLocations = await Promise.all(
      filteredLocations.map(location => geocodeAddress(location))
    );

    const validLocations = geocodedLocations.filter(
      (loc): loc is LocationWithCoordinates => loc !== null
    );

    setLocationsWithCoords(validLocations);

    // Calculate map bounds to show all locations
    if (validLocations.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      validLocations.forEach(loc => {
        bounds.extend({ lat: loc.latitude, lng: loc.longitude });
      });

      if (mapRef.current) {
        mapRef.current.fitBounds(bounds);
        // Add padding to the bounds
        const padding = { top: 50, right: 50, bottom: 50, left: 50 };
        mapRef.current.fitBounds(bounds, padding);
        
        // If only one location, zoom in more
        if (validLocations.length === 1) {
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.setZoom(15);
            }
          }, 100);
        } else if (validLocations.length <= 3) {
          // For a few locations, zoom in a bit more
          setTimeout(() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom && currentZoom < 12) {
                mapRef.current.setZoom(12);
              }
            }
          }, 100);
        }
      }
    }
  }, [filteredLocations, isLoaded, geocodeAddress]);


  // Handle map bounds change to filter visible locations
  const handleBoundsChanged = useCallback(() => {
    if (!mapRef.current || !isLoaded) return;
    
    const bounds = mapRef.current.getBounds();
    if (bounds) {
      setMapBounds(bounds);
      
      // Filter locations that are visible within the current map bounds
      const visibleLocs = filteredLocations.filter(location => {
        // Check if location has coordinates
        const locWithCoords = locationsWithCoords.find(l => l.id === location.id);
        if (!locWithCoords) return false;
        
        // Check if location is within bounds
        return bounds.contains({
          lat: locWithCoords.latitude,
          lng: locWithCoords.longitude
        });
      });
      
      // Sort visible locations alphabetically
      const sortedVisibleLocs = visibleLocs.sort((a, b) => {
        const nameA = (a.name || a.streetLine1 || '').toLowerCase();
        const nameB = (b.name || b.streetLine1 || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setVisibleLocations(sortedVisibleLocs);
    }
  }, [isLoaded, filteredLocations, locationsWithCoords]);

  // Clear filters
  const clearFilters = () => {
    setCustomerFilter('');
    setCustomerTypeFilter('');
    setCityFilter('');
    setZoneFilter('');
  };

  // Navigate to site detail
  const handleLocationClick = (locationId: string) => {
    navigate(`/sites/${locationId}/show`);
  };

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData, currentTenant]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations, currentTenant]);

  useEffect(() => {
    // Always geocode when filteredLocations changes and map is loaded
    if (isLoaded) {
      geocodeAllLocations();
    }
  }, [filteredLocations, isLoaded, geocodeAllLocations]);

  // Add navigation function to window
  useEffect(() => {
    (window as any).navigateToSite = (locationId: string) => {
      handleLocationClick(locationId);
    };

    return () => {
      delete (window as any).navigateToSite;
    };
  }, []);

  // Initialize and update marker clusterer
  useEffect(() => {
    if (!mapRef.current || !isLoaded || !window.google) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Clear existing clusterer completely when locations change
    if (clusterer) {
      clusterer.clearMarkers();
      clusterer.setMap(null);
      setClusterer(null);
    }

    // Create or get info window
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    // Create new markers
    const newMarkers = locationsWithCoords.map((location) => {
      const marker = new window.google.maps.Marker({
        position: { lat: location.latitude, lng: location.longitude },
        icon: getMarkerIcon(location),
        // Don't set title to prevent default tooltip
      });

      marker.addListener('click', () => {
        // Close any existing info window
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }

        // Create info window content
        const content = `
          <div style="padding: 8px; min-width: 200px;">
            <div style="font-weight: bold; margin-bottom: 4px;">
              ${location.name || location.streetLine1}
            </div>
            <div style="font-size: 14px; margin-bottom: 2px;">
              ${location.streetLine1}
            </div>
            <div style="font-size: 14px; margin-bottom: 8px;">
              ${location.city}, ${location.province} ${location.postalCode}
            </div>
            ${location.customerLocations && location.customerLocations.length > 0 ? `
              <div style="border-top: 1px solid #e0e0e0; margin: 4px 0;"></div>
              ${location.customerLocations.slice(0, 2).map(cl => `
                <div style="font-size: 12px;">
                  • ${cl.customer?.displayName || 'Unknown Customer'}
                </div>
              `).join('')}
              ${location.customerLocations.length > 2 ? `
                <div style="font-size: 12px; color: #666;">
                  +${location.customerLocations.length - 2} more
                </div>
              ` : ''}
            ` : ''}
            <button
              onclick="window.navigateToSite('${location.id}')"
              style="width: 100%; margin-top: 8px; padding: 6px; background-color: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer;">
              View Details
            </button>
          </div>
        `;

        // Set content and open
        if (infoWindowRef.current) {
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(mapRef.current, marker);
        }

        setSelectedLocation(location);
      });

      return marker;
    });

    markersRef.current = newMarkers;

    // Always create a new clusterer since we clear it above
    if (mapRef.current && newMarkers.length > 0) {
      const newClusterer = new MarkerClusterer({
        map: mapRef.current,
        markers: newMarkers,
        algorithmOptions: {
          radius: 60,
          maxZoom: 14,
          minPoints: 2,
        },
      });
      setClusterer(newClusterer);
    }

    return () => {
      // Cleanup on unmount
      markersRef.current.forEach(marker => marker.setMap(null));
      if (clusterer) {
        clusterer.clearMarkers();
      }
    };
  }, [locationsWithCoords, isLoaded]);

  const getMarkerIcon = (location: LocationWithCoordinates) => {
    if (!window.google) return undefined;

    const hasCustomers = location.customerLocations && location.customerLocations.length > 0;
    const color = hasCustomers ? '#34A853' : '#EA4335'; // Green if has customers, red if empty

    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
    };
  };

  const LocationItem = ({ location }: { location: Location }) => {
    const primaryCustomer = location.customerLocations?.find(cl => cl.isPrimary)?.customer ||
                           location.customerLocations?.[0]?.customer;

    return (
      <Box 
        sx={{ 
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' }
        }}
        onClick={() => handleLocationClick(location.id)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {location.name || location.streetLine1}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {location.streetLine1}, {location.city}, {location.province}
          </Typography>
          {primaryCustomer && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <BusinessIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="primary" noWrap>
                {primaryCustomer.displayName}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageTitle title="Sites" titleKey="pages.sites" />
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={zones.sort((a, b) => a.name.localeCompare(b.name))}
              getOptionLabel={(option) => option.name || ''}
              value={zones.find(z => z.id === zoneFilter) || null}
              onChange={(_, newValue) => setZoneFilter(newValue?.id || '')}
              renderInput={(params) => (
                <TextField {...params} label="Zone" size="small" fullWidth placeholder="All Zones" />
              )}
              clearOnBlur
              handleHomeEndKeys
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={customers.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))}
              getOptionLabel={(option) => option.displayName || ''}
              value={customers.find(c => c.id === customerFilter) || null}
              onChange={(_, newValue) => setCustomerFilter(newValue?.id || '')}
              renderInput={(params) => (
                <TextField {...params} label="Customer" size="small" fullWidth placeholder="All Customers" />
              )}
              clearOnBlur
              handleHomeEndKeys
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={customerTypes.sort((a, b) => a.name.localeCompare(b.name))}
              getOptionLabel={(option) => option.name || ''}
              value={customerTypes.find(ct => ct.id === customerTypeFilter) || null}
              onChange={(_, newValue) => setCustomerTypeFilter(newValue?.id || '')}
              renderInput={(params) => (
                <TextField {...params} label="Customer Type" size="small" fullWidth placeholder="All Types" />
              )}
              clearOnBlur
              handleHomeEndKeys
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              options={cities.sort()}
              value={cityFilter}
              onChange={(_, newValue) => setCityFilter(newValue || '')}
              renderInput={(params) => (
                <TextField {...params} label="City" size="small" fullWidth placeholder="All Cities" />
              )}
              freeSolo
              clearOnBlur
              handleHomeEndKeys
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content */}
      <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Side - List */}
        <Grid item xs={12} md={3} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {visibleLocations.length} Site{visibleLocations.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {visibleLocations.map(location => (
                <LocationItem key={location.id} location={location} />
              ))}
              
              {visibleLocations.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <LocationIcon sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="body1">
                    No sites match your filters
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Side - Map */}
        <Grid item xs={12} md={9} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', position: 'relative' }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={mapZoom}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
                onBoundsChanged={handleBoundsChanged}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                }}
              >
              </GoogleMap>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};